import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import {
  getApiMessage,
  getLeadInsertedId,
  getLeadSaveMessage,
  getPincodeValueFromRow,
  isLeadSaveSuccess,
  saveLendingPage1Url,
  loanBackendPostUrl,
  taskApiGetUrl,
  taskApiPostUrl,
} from "../../config/taskApi";
import {
  clearApplyFlowOnSuccess,
  getAadhaarNumberSecure,
  getMobileOtpSecure,
} from "../../utils/applyFlowStorage";
import {
  normalizeEmailInput,
  validateEmail,
} from "../../utils/emailValidation";
import { seedApplyLoanProfileFromAadhaar } from "../../utils/seedApplyLoanProfile";
import { pauseEnquiryAutosave, scheduleSaveEnquiry } from "../../utils/saveEnquiry";
import "./LandingLoanForm.css";

const MOBILE_REGEX = /^[6-9]\d{9}$/;

const resolveUtmSource = () => {
  if (typeof window === "undefined") return "";
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("utm_source");
    if (fromUrl && String(fromUrl).trim()) {
      const value = String(fromUrl).trim();
      try {
        localStorage.setItem("utm_source", value);
      } catch {
        /* ignore */
      }
      return value;
    }
  } catch {
    /* ignore */
  }
  try {
    return String(localStorage.getItem("utm_source") || "").trim();
  } catch {
    return "";
  }
};

/** Prefer DigiLocker full_address; else build from KYC address parts. */
const resolveAadhaarFullAddress = (aadhaar = {}, seeded = {}) => {
  const direct = [
    aadhaar.full_address,
    aadhaar.fullAddress,
    aadhaar.aadhaar_address,
    typeof aadhaar.address === "string" ? aadhaar.address : "",
    seeded.full_address,
    seeded.aadhaar_address,
    seeded.address,
  ]
    .map((v) => String(v || "").trim())
    .find(Boolean);
  if (direct) return direct;

  const addrObj =
    aadhaar.address && typeof aadhaar.address === "object" ? aadhaar.address : {};
  return [
    aadhaar.care_of || aadhaar.careOf || addrObj.care_of,
    aadhaar.house || addrObj.house,
    aadhaar.street || addrObj.street,
    aadhaar.landmark || addrObj.landmark,
    aadhaar.locality || aadhaar.loc || addrObj.locality || addrObj.loc,
    aadhaar.post_office || aadhaar.postOffice || addrObj.post_office,
    aadhaar.vtc || aadhaar.vtcName || aadhaar.village || addrObj.vtc,
    aadhaar.district || aadhaar.dist || addrObj.district || addrObj.dist,
    aadhaar.state || addrObj.state,
    aadhaar.pincode || aadhaar.pc || addrObj.pincode || addrObj.pc,
    aadhaar.country || addrObj.country,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(", ");
};

const Req = () => <span className="req">*</span>;

const FieldError = ({ message }) => (
  <p className="fw-landing-loan-field__error" role={message ? "alert" : undefined}>
    {message || "\u00a0"}
  </p>
);

const parseFullName = (fullName) => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: "", middleName: "", surname: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", surname: "" };
  if (parts.length === 2) return { firstName: parts[0], middleName: "", surname: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    surname: parts[parts.length - 1],
  };
};

const normalizeGender = (value) => {
  const g = String(value || "").trim().toUpperCase();
  if (g === "M" || g === "MALE") return "MALE";
  if (g === "F" || g === "FEMALE") return "FEMALE";
  if (g) return "OTHER";
  return "";
};

const toDateInputValue = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const dmy = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  const dmyText = text.match(/^(\d{2})[-\s]([A-Za-z]{3})[-\s](\d{4})$/);
  if (dmyText) {
    const months = {
      jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
      jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
    };
    const mm = months[dmyText[2].toLowerCase()];
    if (mm) return `${dmyText[3]}-${mm}-${dmyText[1]}`;
  }

  return text;
};

const buildPanPayloadFields = (loan) => ({
  pan_responseCode: loan.pan_responseCode || "",
  pan_responseMessage: loan.pan_responseMessage || "",
  pan_referenceId: loan.pan_referenceId || "",
  pan_firstName: loan.pan_firstName || "",
  pan_lastName: loan.pan_lastName || "",
  pan_address: loan.pan_address || "",
  pan_gender: loan.pan_gender || "",
  pan_aadhaarLinked: loan.pan_aadhaarLinked || "",
  pan_dob: loan.pan_dob || "",
  pan_fullName: loan.pan_fullName || "",
  pan_category: loan.pan_category || "",
  pan_maskedAadhaarNumber: loan.pan_maskedAadhaarNumber || "",
  pan_verifiedPan: loan.pan_verifiedPan || loan.pan || "",
});

const LandingLoanForm = ({
  onBack,
  onNext,
  leadSource = "landing",
  // "phpDual" = /landing (saveLendingPage.php + submit.php)
  // "taskApi1" = /apply-loan (only TaskApi/saveLendingPage1)
  leadSaveMode = "phpDual",
}) => {
  const [profile, setProfile] = useState(null);
  const [mobile, setMobile] = useState("");
  const [pan, setPan] = useState("");
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [altMobile, setAltMobile] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [surname, setSurname] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [officialEmail, setOfficialEmail] = useState("");
  const [obligations, setObligations] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [pincode, setPincode] = useState("");
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const [statesLoadError, setStatesLoadError] = useState("");
  const [citiesLoadError, setCitiesLoadError] = useState("");
  const [pincodesLoadError, setPincodesLoadError] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submitLockRef = useRef(false);
  const citiesRequestRef = useRef(0);
  const pincodesRequestRef = useRef(0);

  useEffect(() => {
    const seeded = seedApplyLoanProfileFromAadhaar();
    setProfile(seeded);

    let aadhaar = {};
    try {
      aadhaar = JSON.parse(localStorage.getItem("aadhaarData") || "{}");
    } catch {
      aadhaar = {};
    }

    const fullNameValue =
      aadhaar.name ||
      aadhaar.fullName ||
      seeded.pan_fullName ||
      [seeded.firstName, seeded.lastName].filter(Boolean).join(" ");
    const parsedName = parseFullName(fullNameValue);

    setMobile(
      String(seeded.mobile || localStorage.getItem("mobileNumber") || "")
        .replace(/\D/g, "")
        .slice(-10)
    );
    setPan(String(seeded.pan || seeded.pan_verifiedPan || "").toUpperCase());
    setFullName(String(fullNameValue || "").trim());
    setAddress(resolveAadhaarFullAddress(aadhaar, seeded));
    setAltMobile(String(seeded.alternate_mobile || seeded.altMobile || "").replace(/\D/g, "").slice(-10));
    setLoanAmount(String(seeded.loan_amount || seeded.loanAmount || ""));
    setMonthlyIncome(String(seeded.monthly_income || seeded.monthlyIncome || ""));
    setFirstName(String(seeded.firstName || parsedName.firstName || ""));
    setMiddleName(String(seeded.middleName || seeded.middle_name || parsedName.middleName || ""));
    setSurname(String(seeded.lastName || seeded.sur_name || parsedName.surname || ""));
    setDob(toDateInputValue(seeded.dob || aadhaar.dateOfBirth || aadhaar.dob || ""));
    setGender(normalizeGender(seeded.gender || aadhaar.gender || seeded.pan_gender || ""));
    setEmail(normalizeEmailInput(seeded.email || seeded.email_personal || ""));
    setOfficialEmail(normalizeEmailInput(seeded.email_official || seeded.officialEmail || ""));
    setObligations(String(seeded.obligations || seeded.obligation || ""));
    setStateId(String(seeded.state_id || ""));
    setCityId(String(seeded.city_id || ""));
    setPincode(String(seeded.pincode || ""));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    axios
      .get(taskApiGetUrl("getState"), { signal: controller.signal })
      .then((res) => {
        if (res.data?.Status === 1 && Array.isArray(res.data.Data)) {
          setStates(res.data.Data);
          setStatesLoadError("");
        } else {
          setStatesLoadError(getApiMessage(res.data, "Failed to load states."));
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        setStatesLoadError(
          getApiMessage(err?.response?.data, "Could not load states.")
        );
      });

    return () => controller.abort();
  }, []);

  const loadCitiesForState = async (sid, signal) => {
    if (!sid) {
      setCities([]);
      return [];
    }

    const requestId = ++citiesRequestRef.current;
    setLoadingCities(true);
    setCitiesLoadError("");

    try {
      const resp = await axios.post(
        taskApiPostUrl("getCity"),
        { state_id: sid },
        { signal }
      );
      if (requestId !== citiesRequestRef.current) return [];

      if (resp.data?.Status === 1 && Array.isArray(resp.data.Data)) {
        setCities(resp.data.Data);
        setCitiesLoadError("");
        return resp.data.Data;
      }

      setCities([]);
      setCitiesLoadError(getApiMessage(resp.data, "Failed to load cities."));
      return [];
    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return [];
      if (requestId !== citiesRequestRef.current) return [];
      setCities([]);
      setCitiesLoadError(getApiMessage(err?.response?.data, "Could not load cities."));
      return [];
    } finally {
      if (requestId === citiesRequestRef.current) setLoadingCities(false);
    }
  };

  const loadPincodesForCity = async (cid, signal) => {
    if (!cid) {
      setPincodes([]);
      return [];
    }

    const requestId = ++pincodesRequestRef.current;
    setLoadingPincodes(true);
    setPincodesLoadError("");

    try {
      const resp = await axios.post(
        taskApiPostUrl("getPincodeByCityId"),
        { city_id: cid },
        { signal }
      );
      if (requestId !== pincodesRequestRef.current) return [];

      const rows = resp.data?.Data || resp.data?.data || [];
      if (Array.isArray(rows) && rows.length) {
        setPincodes(rows);
        setPincodesLoadError("");
        return rows;
      }

      setPincodes([]);
      setPincodesLoadError(getApiMessage(resp.data, "No pincodes available."));
      return [];
    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return [];
      if (requestId !== pincodesRequestRef.current) return [];
      setPincodes([]);
      setPincodesLoadError(getApiMessage(err?.response?.data, "Could not load pincodes."));
      return [];
    } finally {
      if (requestId === pincodesRequestRef.current) setLoadingPincodes(false);
    }
  };

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      return undefined;
    }
    const controller = new AbortController();
    loadCitiesForState(stateId, controller.signal);
    return () => controller.abort();
  }, [stateId]);

  useEffect(() => {
    if (!cityId) {
      setPincodes([]);
      return undefined;
    }
    const controller = new AbortController();
    loadPincodesForCity(cityId, controller.signal);
    return () => controller.abort();
  }, [cityId]);

  useEffect(() => {
    const patch = { step: "loan_details", source: leadSource };
    if (loanAmount) patch.loan_amount = loanAmount;
    if (email && !validateEmail(normalizeEmailInput(email))) {
      patch.email = normalizeEmailInput(email);
      patch.email_personal = patch.email;
    }
    if (cityId) patch.city_id = String(cityId);
    if (/^\d{6}$/.test(String(pincode || "").trim())) patch.pincode = String(pincode).trim();
    scheduleSaveEnquiry(patch);
  }, [loanAmount, email, cityId, pincode, leadSource]);

  const setFieldError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message || "" }));
  };

  const validateForm = () => {
    const next = {};

    if (!String(fullName || "").trim()) {
      next.fullName = "Full name is missing. Please complete DigiLocker again.";
    }

    if (!MOBILE_REGEX.test(altMobile)) {
      next.altMobile = "Enter a valid 10-digit alternate mobile";
    } else if (altMobile === mobile) {
      next.altMobile = "Alternate mobile must be different from verified mobile";
    }

    if (!loanAmount) next.loanAmount = "Loan amount is required";
    if (!monthlyIncome) next.monthlyIncome = "Monthly income is required";

    const normalizedEmail = normalizeEmailInput(email);
    const emailErr = validateEmail(normalizedEmail);
    if (emailErr) next.email = emailErr;

    if (officialEmail.trim()) {
      const officialErr = validateEmail(normalizeEmailInput(officialEmail));
      if (officialErr && officialErr !== "Email is required") {
        next.officialEmail = officialErr;
      } else if (
        normalizedEmail &&
        normalizeEmailInput(officialEmail).toLowerCase() === normalizedEmail.toLowerCase()
      ) {
        next.officialEmail = "Official email must be different from personal email";
      }
    }

    if (!String(address || "").trim()) next.address = "Address is required";
    if (!stateId) next.stateId = "Please select your state";
    if (!cityId) next.cityId = "Please select your city";

    const pinTrim = String(pincode || "").trim();
    if (!pinTrim) next.pincode = "Please select your pincode";
    else if (!/^\d{6}$/.test(pinTrim)) next.pincode = "Please select a valid pincode";

    if (!termsAccepted) next.termsAccepted = "Please accept terms & conditions";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (submitting || submitLockRef.current) return;

    const verifiedPan = String(pan || "").toUpperCase().trim();
    if (verifiedPan.length !== 10) {
      setSubmitError("PAN verification missing. Please go back to identity step.");
      return;
    }

    if (!validateForm()) {
      setTimeout(() => {
        const firstError = document.querySelector(".fw-landing-loan-form .has-error");
        firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    pauseEnquiryAutosave();

    const loan = profile || seedApplyLoanProfileFromAadhaar();
    const aadhaarNumber = String(getAadhaarNumberSecure() || "").replace(/\D/g, "");
    const mobileOtpValue = String(getMobileOtpSecure() || loan.otp || "").trim();
    const selectedState = states.find((s) => String(s.state_id) === String(stateId));
    const selectedCity = cities.find((c) => String(c.city_id) === String(cityId));
    const emailForSubmit = normalizeEmailInput(email);
    const officialForSubmit = normalizeEmailInput(officialEmail);

    const nameParts = parseFullName(fullName);
    const panFields = buildPanPayloadFields({ ...loan, pan: verifiedPan });
    const genderForSubmit = (() => {
      const g = String(gender || "").trim().toUpperCase();
      if (g === "M" || g === "MALE") return "Male";
      if (g === "F" || g === "FEMALE") return "Female";
      if (g) return "Other";
      return "";
    })();
    const submitStateName = selectedState?.state || "";
    const submitCityName = selectedCity?.city || "";
    const submitPincode = String(pincode).trim();
    const aadhaarLastFour =
      aadhaarNumber.length >= 4 ? aadhaarNumber.slice(-4) : "";

    let aadhaarData = {};
    try {
      aadhaarData = JSON.parse(localStorage.getItem("aadhaarData") || "{}");
    } catch {
      aadhaarData = {};
    }

    const payload = {
      loan_amount: Number(loanAmount) || String(loanAmount).trim(),
      monthly_income: String(monthlyIncome).trim(),
      first_name: (firstName || nameParts.firstName || "").trim(),
      middle_name: (middleName || nameParts.middleName || "").trim(),
      sur_name: (surname || nameParts.surname || "").trim(),
      full_name: String(fullName || "").trim(),
      Name: String(fullName || "").trim(),
      pan: verifiedPan,
      pan_status: true,
      ...panFields,
      dob,
      gender: genderForSubmit,
      mobile,
      alternate_mobile: altMobile.trim(),
      otp: mobileOtpValue,
      email_personal: emailForSubmit,
      email_official: officialForSubmit,
      obligations: obligations ? Number(obligations) : 0,
      state_id: Number(stateId) || String(stateId),
      city: String(cityId),
      city_id: Number(cityId) || String(cityId),
      pin: submitPincode,
      pincode: submitPincode,
      address: String(address || "").trim(),
      term_and_condition: termsAccepted ? "YES" : "NO",
      mobile_verified: localStorage.getItem("mobile_verified") || "true",
      // Same source field apply-now uses (admin SOURCE column).
      // /landing → "landing", /apply-loan → "website"
      source: String(leadSource || "landing").trim() || "landing",
      utm_source: resolveUtmSource(),
      aadhaar_number: aadhaarNumber,
      aadhaar_last_four: aadhaarLastFour,
      state: submitStateName,
      city_name: submitCityName,
    };

    try {
      // /apply-loan → single TaskApi saveLendingPage1 only
      if (leadSaveMode === "taskApi1") {
        const resp = await axios.post(saveLendingPage1Url(), payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        });

        if (!isLeadSaveSuccess(resp.data)) {
          setSubmitError(
            getLeadSaveMessage(resp.data, "Submit failed. Please try again.")
          );
          return;
        }

        const lead_id = getLeadInsertedId(resp.data);
        if (lead_id) {
          localStorage.setItem("currentLeadId", String(lead_id));
        }

        localStorage.removeItem("earlyEnquiryId");
        try {
          sessionStorage.removeItem("applyFlowEarlyEnquiryId");
        } catch {
          /* ignore */
        }
        clearApplyFlowOnSuccess();
        if (lead_id) {
          localStorage.setItem("currentLeadId", String(lead_id));
        }
        if (typeof onNext === "function") onNext();
        return;
      }

      // /landing → saveLendingPage.php + submit.php (same as apply-now pair)
      const resp = await axios.post(
        loanBackendPostUrl("saveLendingPage.php"),
        payload,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (!isLeadSaveSuccess(resp.data)) {
        setSubmitError(getLeadSaveMessage(resp.data, "Submit failed. Please try again."));
        return;
      }

      const lead_id = getLeadInsertedId(resp.data);
      if (!lead_id) {
        setSubmitError(
          getLeadSaveMessage(
            resp.data,
            "Application saved but lead id missing. Please try again."
          )
        );
        return;
      }

      localStorage.setItem("currentLeadId", String(lead_id));

      const formData = new FormData();
      formData.append("lead_id", String(lead_id));
      formData.append("pan", verifiedPan);
      formData.append("pan_verified", "true");
      formData.append("pan_status", "true");
      formData.append("pan_responseCode", panFields.pan_responseCode);
      formData.append("pan_responseMessage", panFields.pan_responseMessage);
      formData.append("pan_referenceId", panFields.pan_referenceId);
      formData.append("pan_firstName", panFields.pan_firstName);
      formData.append("pan_lastName", panFields.pan_lastName);
      formData.append("pan_address", panFields.pan_address);
      formData.append("pan_gender", panFields.pan_gender);
      formData.append("pan_aadhaarLinked", panFields.pan_aadhaarLinked);
      formData.append("pan_dob", panFields.pan_dob);
      formData.append("pan_fullName", panFields.pan_fullName);
      formData.append("pan_maskedAadhaarNumber", panFields.pan_maskedAadhaarNumber);
      formData.append("pan_category", panFields.pan_category);
      formData.append("pan_verifiedPan", panFields.pan_verifiedPan || verifiedPan);
      formData.append("mobile", mobile);
      formData.append(
        "mobile_verified",
        localStorage.getItem("mobile_verified") || "true"
      );
      formData.append("otp", mobileOtpValue);
      formData.append("aadhar_no", aadhaarLastFour);
      formData.append("aadhaar_mobile", mobile);
      formData.append("firstName", (firstName || nameParts.firstName || "").trim());
      formData.append("lastName", (surname || nameParts.surname || "").trim());
      formData.append("dob", dob);
      formData.append("address", String(address || "").trim());
      formData.append("stateId", String(stateId));
      formData.append("cityId", String(cityId));
      formData.append("pincode", submitPincode);
      formData.append("gender", genderForSubmit);
      formData.append("monthlyIncome", String(monthlyIncome).trim());
      formData.append("monthly_income", String(monthlyIncome).trim());
      formData.append("loanAmount", String(loanAmount).trim());
      formData.append("obligation", obligations ? String(obligations) : "0");
      formData.append("email", emailForSubmit);
      formData.append("state", submitStateName);
      formData.append("city", submitCityName);
      formData.append("source", String(leadSource || "landing").trim() || "landing");

      formData.append(
        "aadhaar_number",
        aadhaarNumber ||
          aadhaarData?.aadhaarNumber ||
          aadhaarData?.aadhaar_number ||
          aadhaarData?.referenceId ||
          ""
      );
      formData.append(
        "aadhaar_name",
        aadhaarData?.name || String(fullName || "").trim()
      );
      formData.append(
        "aadhaar_dob",
        aadhaarData?.dateOfBirth || aadhaarData?.dob || dob || ""
      );
      formData.append(
        "aadhaar_gender",
        aadhaarData?.gender || genderForSubmit || ""
      );
      formData.append(
        "aadhaar_address",
        String(address || "").trim() || aadhaarData?.address || ""
      );
      formData.append("aadhaar_pincode", aadhaarData?.pincode || submitPincode);
      formData.append("aadhaar_state", aadhaarData?.state || submitStateName);
      formData.append(
        "aadhaar_district",
        aadhaarData?.district || aadhaarData?.vtcName || submitCityName
      );

      formData.append(
        "docs_master_ids",
        JSON.stringify({
          aadharfront: "",
          aadhar: "",
          pan_card: "",
          bank_statement: "",
          salary_slip: "",
          utility_bill: "",
        })
      );

      const saveRes = await axios.post(
        loanBackendPostUrl("submit.php"),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }
      );

      if (!isLeadSaveSuccess(saveRes.data)) {
        setSubmitError(
          getLeadSaveMessage(
            saveRes.data,
            "Application could not be submitted. Please try again."
          )
        );
        return;
      }

      localStorage.setItem("currentLeadId", String(lead_id));
      localStorage.removeItem("earlyEnquiryId");
      try {
        sessionStorage.removeItem("applyFlowEarlyEnquiryId");
      } catch {
        /* ignore */
      }
      clearApplyFlowOnSuccess();
      localStorage.setItem("currentLeadId", String(lead_id));
      if (typeof onNext === "function") onNext();
    } catch (err) {
      setSubmitError(
        getLeadSaveMessage(
          err?.response?.data,
          err?.message || "Submit failed. Please try again."
        )
      );
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (!profile) {
    return (
      <div className="fw-landing-loan-form fw-landing-loan-form--loading">
        <p>Preparing your application…</p>
      </div>
    );
  }

  const renderSelect = ({
    id,
    label,
    required,
    value,
    onChange,
    disabled,
    placeholder,
    options,
    errorKey,
  }) => (
    <div className={`fw-landing-loan-field ${errors[errorKey] ? "has-error" : ""}`}>
      <label htmlFor={id}>
        {label} {required ? <Req /> : null}
      </label>
      <div className="fw-landing-loan-select-wrap">
        <select
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={!value ? "is-placeholder" : ""}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <FiChevronDown aria-hidden="true" />
      </div>
      <FieldError message={errors[errorKey]} />
    </div>
  );

  const renderInput = ({
    id,
    label,
    required,
    value,
    onChange,
    type = "text",
    placeholder,
    maxLength,
    inputMode,
    disabled,
    errorKey,
    fullWidth = false,
  }) => (
    <div
      className={`fw-landing-loan-field ${fullWidth ? "fw-landing-loan-field--full" : ""} ${
        errors[errorKey] ? "has-error" : ""
      }`}
    >
      <label htmlFor={id}>
        {label} {required ? <Req /> : null}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        disabled={disabled}
        readOnly={disabled}
      />
      <FieldError message={errors[errorKey]} />
    </div>
  );

  return (
    <form className="fw-landing-loan-form" onSubmit={handleSubmit} noValidate>
      <div className="fw-landing-loan-grid">
        {renderInput({
          id: "fullName",
          label: "Full Name",
          required: true,
          value: fullName,
          onChange: () => {},
          placeholder: "From DigiLocker",
          disabled: true,
          errorKey: "fullName",
        })}

        {renderInput({
          id: "monthlyIncome",
          label: "Monthly Income",
          required: true,
          value: monthlyIncome,
          onChange: (e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 8);
            setMonthlyIncome(v);
            setFieldError("monthlyIncome", "");
          },
          placeholder: "Monthly income",
          inputMode: "numeric",
          errorKey: "monthlyIncome",
        })}

        {renderInput({
          id: "loanAmount",
          label: "Loan Amount",
          required: true,
          value: loanAmount,
          onChange: (e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
            setLoanAmount(v);
            setFieldError("loanAmount", "");
          },
          placeholder: "Required amount",
          inputMode: "numeric",
          errorKey: "loanAmount",
        })}

        {renderInput({
          id: "altMobile",
          label: "Alternate Mobile",
          required: true,
          value: altMobile,
          onChange: (e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
            setAltMobile(v);
            setFieldError("altMobile", "");
          },
          placeholder: "10-digit mobile number",
          maxLength: 10,
          inputMode: "numeric",
          errorKey: "altMobile",
        })}

        {renderInput({
          id: "email",
          label: "Email",
          required: true,
          value: email,
          onChange: (e) => {
            setEmail(e.target.value);
            setFieldError("email", "");
          },
          type: "email",
          placeholder: "name@email.com",
          errorKey: "email",
        })}

        {renderInput({
          id: "officialEmail",
          label: "Official Email",
          required: false,
          value: officialEmail,
          onChange: (e) => {
            setOfficialEmail(e.target.value);
            setFieldError("officialEmail", "");
          },
          type: "email",
          placeholder: "work@company.com",
          errorKey: "officialEmail",
        })}

        {renderInput({
          id: "obligations",
          label: "Obligations",
          required: false,
          value: obligations,
          onChange: (e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 7);
            setObligations(v);
          },
          placeholder: "Monthly EMIs / obligations",
          inputMode: "numeric",
          errorKey: "obligations",
        })}

        {renderSelect({
          id: "stateId",
          label: "State",
          required: true,
          value: stateId,
          onChange: (e) => {
            setStateId(e.target.value);
            setCityId("");
            setPincode("");
            setFieldError("stateId", "");
          },
          disabled: !states.length && !!statesLoadError,
          placeholder: states.length ? "Select State" : "Loading states...",
          options: states.map((state) => ({
            value: String(state.state_id),
            label: state.state,
          })),
          errorKey: "stateId",
        })}

        {renderSelect({
          id: "cityId",
          label: "City",
          required: true,
          value: cityId,
          onChange: (e) => {
            setCityId(e.target.value);
            setPincode("");
            setFieldError("cityId", "");
          },
          disabled: !stateId || loadingCities,
          placeholder: !stateId
            ? "Select state first"
            : loadingCities
              ? "Loading cities..."
              : cities.length
                ? "Select City"
                : "No cities available",
          options: cities.map((city) => ({
            value: String(city.city_id),
            label: city.city,
          })),
          errorKey: "cityId",
        })}

        {renderSelect({
          id: "pincode",
          label: "Pincode",
          required: true,
          value: pincode,
          onChange: (e) => {
            setPincode(e.target.value);
            setFieldError("pincode", "");
          },
          disabled:
            !cityId || loadingPincodes || (!pincodes.length && !loadingPincodes),
          placeholder: !cityId
            ? "Select city first"
            : loadingPincodes
              ? "Loading pincodes..."
              : pincodes.length
                ? "Select Pincode"
                : "No pincodes available",
          options: pincodes.map((pin) => {
            const value = getPincodeValueFromRow(pin);
            return { value, label: value };
          }),
          errorKey: "pincode",
        })}

        {(statesLoadError || citiesLoadError || pincodesLoadError) && (
          <div className="fw-landing-loan-error fw-landing-loan-error--full">
            {statesLoadError || citiesLoadError || pincodesLoadError}
          </div>
        )}

        <div
          className={`fw-landing-loan-field fw-landing-loan-field--full ${
            errors.address ? "has-error" : ""
          }`}
        >
          <label htmlFor="address">
            Address <Req />
          </label>
          <textarea
            id="address"
            rows={2}
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setFieldError("address", "");
            }}
            placeholder="Full address"
          />
          <FieldError message={errors.address} />
        </div>
      </div>

      <div
        className={`fw-landing-loan-terms ${
          errors.termsAccepted ? "has-error" : ""
        }`}
      >
        <label>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setFieldError("termsAccepted", "");
            }}
          />
          <span>
            I hereby declare that all the above information provided by me is
            true, correct, and verified to the best of my knowledge. I agree to
            the{" "}
            <Link
              to="/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms &amp; Conditions
            </Link>
            <Req />
          </span>
        </label>
        <FieldError message={errors.termsAccepted} />
      </div>

      {submitError && (
        <p
          className="fw-landing-loan-error fw-landing-loan-error--submit"
          role="alert"
        >
          {submitError}
        </p>
      )}

      <div className="apply-flow-actions fw-landing-loan-actions">
        <button
          type="button"
          className="apply-flow-btn apply-flow-btn--secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </button>
        <button
          type="submit"
          className="apply-flow-btn apply-flow-btn--primary"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default LandingLoanForm;
